import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useDashboardStore } from '../../store/dashboardStore';
import { IconSidebar } from './IconSidebar';
import { MonthCalendarPanel } from './MonthCalendarPanel';
import { PatientListPanel } from './PatientListPanel';
import { SchedulePanel } from './SchedulePanel';
import { SchedulePatientListPanel } from './SchedulePatientListPanel';
import { HandoverDetailPanel } from './HandoverDetailPanel';
import { SettingsPanel } from './SettingsPanel';
import { StaffListPanel } from './StaffListPanel';
import { Header } from './Header';
import { BottomMenuBar } from './BottomMenuBar';
// 【変更】PatientFormPanelとStaffFormPanelをインポート
import { PatientFormPanel } from './PatientFormPanel';
import { StaffFormPanel } from './StaffFormPanel';
import { PinChangeDialog } from './PinChangeDialog';
import { SpeechDictionaryPanel } from './SpeechDictionaryPanel';

interface DashboardLayoutProps {
  children?: React.ReactNode;
  staff?: any;
  onLogout?: () => Promise<void>;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, staff, onLogout }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isStandalone = useMediaQuery('(display-mode: standalone)');
  const { 
    showDatePanel,
    showDetailPanel,
    showSettingsPanel,
    showAllPatientListPanel,
    showStaffListPanel,
    // 【変更】患者フォームパネルの状態を取得
    showPatientFormPanel,
    editingPatient, // 【追加】編集中の患者データをストアから取得
    // 【追加】スタッフフォームパネルの状態を取得
    showStaffFormPanel,
    editingStaff,
    staffFormRevision, // この行を追加
    // ▼▼▼ 以下カウンターを全て追加 ▼▼▼
    datePanelRevision,
    detailPanelRevision,
    settingsPanelRevision,
    patientListRevision,
    staffListRevision,
    // 設定側日付パネル（最小制御）
    showSettingsDatePanel,
    settingsDatePanelRevision,
    showSchedulePanel,
    schedulePanelRevision,
    scheduleSelectedStaffId,
    showSchedulePatientListPanel,
    schedulePatientListRevision,
    schedulePanelSource,
    showWorkflowStaffListPanel,
    workflowStaffListRevision,
    showSpeechDictionaryPanel,
    speechDictionaryRevision,
    settingsReturningToRoot,
    setSettingsReturningToRoot,
    workflowRootRevealFromSettings,
    setWorkflowRootRevealFromSettings,
    settingsRootRevealFromWorkflow,
    setSettingsRootRevealFromWorkflow,
    // 【追加】戻る操作を識別するフラグ
    setIsNavigatingBack,
    showPinChangeDialog,
    setShowPinChangeDialog,
    setPinChangeMessage,
  } = useDashboardStore();

  // 【追加】スクロールコンテナを参照するためのrefを作成
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // 【追加】パネル領域の実幅をCSS変数へ同期（100vw基準のズレ対策）
  useEffect(() => {
    if (isMobile) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const updatePanelAreaWidthVar = () => {
      container.style.setProperty('--panel-area-w', `${container.clientWidth}px`);
    };

    updatePanelAreaWidthVar();

    const ro = new ResizeObserver(() => {
      updatePanelAreaWidthVar();
    });
    ro.observe(container);

    const handleWindowResize = () => {
      updatePanelAreaWidthVar();
      // ウィンドウリサイズ時のみ「右端優先」を補正（開閉時のスクロール演出を邪魔しない）
      const maxLeft = container.scrollWidth - container.clientWidth;
      container.scrollTo({
        left: Math.max(0, maxLeft),
        behavior: 'auto',
      });
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isMobile]);

  // スマホ版: ヘッダー実高を計測し --header-actual を設定（パネルの余白調整用）
  useLayoutEffect(() => {
    if (!isMobile) return;
    const el = headerRef.current;
    if (!el) return;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-actual', `${h}px`);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  // 【追加】パネルの表示状態が変化するたびに自動スクロールを実行
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollToRightEdge = () => {
        const maxLeft = container.scrollWidth - container.clientWidth;
        container.scrollTo({
          left: Math.max(0, maxLeft),
          // デスクトップ: smooth だと列の増減と重なり「右に流れた」ように見えやすい
          behavior: isMobile ? 'smooth' : 'auto',
        });
      };

      // まずは次フレームで反映（レイアウト確定後）
      requestAnimationFrame(scrollToRightEdge);
      // アニメーション等でscrollWidthが変化するケースに備えて追いかける
      setTimeout(scrollToRightEdge, 350);
    }
  }, [
    showDatePanel,
    showDetailPanel,
    showSettingsPanel,
    showAllPatientListPanel,
    showStaffListPanel,
    showPatientFormPanel,
    showStaffFormPanel,
    showSettingsDatePanel,
    showSchedulePanel,
    showSchedulePatientListPanel,
    showWorkflowStaffListPanel,
    showSpeechDictionaryPanel,
    isMobile,
  ]);

  if (isMobile) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <Header ref={headerRef} userName={staff?.name} isMobile={true} />
        <div
          className={`flex-1 overflow-hidden pt-[var(--header-actual,var(--header-total))] min-h-0 ${
            !isStandalone ? 'pb-[var(--bottom-menu-total)]' : ''
          }`}
        >
          {/* パネルが表示されていない場合は、中央にアイコンメニューを表示 */}
          {!showWorkflowStaffListPanel && !showDatePanel && !showDetailPanel && !showSettingsPanel && !showSpeechDictionaryPanel && !showAllPatientListPanel && !showStaffListPanel && !showPatientFormPanel && !showStaffFormPanel && !showSettingsDatePanel && !showSchedulePanel && !showSchedulePatientListPanel && !settingsReturningToRoot && !workflowRootRevealFromSettings && !settingsRootRevealFromWorkflow && (
            <div className="flex-1 flex items-center justify-center">
              <IconSidebar />
            </div>
          )}

          {/* フロー切り替え時は条件分岐で即時切り替え（スライドアニメーションなし） */}
          {(() => {
            const isWorkflowActive =
              showWorkflowStaffListPanel ||
              showDatePanel ||
              showDetailPanel ||
              (showSchedulePanel && schedulePanelSource === 'workflow') ||
              (showSchedulePatientListPanel && schedulePanelSource === 'workflow');
            const isSettingsActive =
              showSettingsPanel ||
              showAllPatientListPanel ||
              showStaffListPanel ||
              showPatientFormPanel ||
              showStaffFormPanel ||
              showSettingsDatePanel ||
              (showSchedulePanel && schedulePanelSource === 'settings') ||
              (showSchedulePatientListPanel && schedulePanelSource === 'settings');
            const isSpeechDictionaryActive = showSpeechDictionaryPanel;

            const clearBackFlagsOnExit = () => {
              setIsNavigatingBack(false);
              const s = useDashboardStore.getState();
              if (s.settingsReturningToRoot) {
                setSettingsReturningToRoot(false);
              }
              if (s.workflowRootRevealFromSettings) {
                setWorkflowRootRevealFromSettings(false);
              }
              if (s.settingsRootRevealFromWorkflow) {
                setSettingsRootRevealFromWorkflow(false);
              }
            };

            if (!isWorkflowActive && !isSettingsActive && !isSpeechDictionaryActive) {
              return null;
            }

            return (
              <AnimatePresence onExitComplete={clearBackFlagsOnExit}>
                {showWorkflowStaffListPanel && (
                  <StaffListPanel key={`workflow-staff-list-${workflowStaffListRevision}`} context="workflow" isMobile={true} />
                )}
                {showDatePanel && (
                  <MonthCalendarPanel key={`date-panel-${datePanelRevision}`} context="workflow" isMobile={true} />
                )}
                {showDetailPanel && (
                  <HandoverDetailPanel key={`detail-panel-${detailPanelRevision}`} isMobile={true} />
                )}
                {showSchedulePanel && schedulePanelSource === 'workflow' && (
                  <SchedulePanel key={`schedule-panel-wf-${schedulePanelRevision}-${scheduleSelectedStaffId || 'no-staff'}`} isMobile={true} />
                )}
                {showSchedulePatientListPanel && schedulePanelSource === 'workflow' && (
                  <SchedulePatientListPanel key={`schedule-patients-wf-${schedulePatientListRevision}`} isMobile={true} />
                )}

                {showSettingsPanel && (
                  <SettingsPanel key={`settings-panel-${settingsPanelRevision}`} isMobile={true} />
                )}
                {showAllPatientListPanel && (
                  <PatientListPanel key={`all-patients-${patientListRevision}`} context="settings" isMobile={true} />
                )}
                {showStaffListPanel && (
                  <StaffListPanel key={`staff-list-${staffListRevision}`} context="settings" isMobile={true} />
                )}
                {showPatientFormPanel && (
                  <PatientFormPanel key={`patient-form-${editingPatient?.id ?? 'new'}`} isMobile={true} />
                )}
                {showStaffFormPanel && (
                  <StaffFormPanel key={`${editingStaff?.id || 'new'}-${staffFormRevision}`} isMobile={true} />
                )}
                {showSettingsDatePanel && (
                  <MonthCalendarPanel key={`settings-date-panel-${settingsDatePanelRevision}`} context="settings" isMobile={true} />
                )}
                {showSchedulePanel && schedulePanelSource === 'settings' && (
                  <SchedulePanel key={`schedule-panel-st-${schedulePanelRevision}-${scheduleSelectedStaffId || 'no-staff'}`} isMobile={true} />
                )}
                {showSchedulePatientListPanel && schedulePanelSource === 'settings' && (
                  <SchedulePatientListPanel key={`schedule-patients-st-${schedulePatientListRevision}`} isMobile={true} />
                )}
                {showSpeechDictionaryPanel && (
                  <SpeechDictionaryPanel key={`speech-dict-m-${speechDictionaryRevision}`} isMobile={true} />
                )}
              </AnimatePresence>
            );
          })()}
        </div>
        <BottomMenuBar onLogout={onLogout} useFixedPosition={!isStandalone} />
        <PinChangeDialog
          key={showPinChangeDialog ? 'open' : 'closed'}
          open={showPinChangeDialog}
          onOpenChange={setShowPinChangeDialog}
          onError={(msg) => setPinChangeMessage({ type: 'error', text: msg })}
          onLogoutAfterSuccess={onLogout}
        />
      </div>
    );
  }

  return (
    <>
      <Header userName={staff?.name} onLogout={onLogout} />
      <PinChangeDialog
        key={showPinChangeDialog ? 'open' : 'closed'}
        open={showPinChangeDialog}
        onOpenChange={setShowPinChangeDialog}
        onError={(msg) => setPinChangeMessage({ type: 'error', text: msg })}
        onLogoutAfterSuccess={onLogout}
      />
      <div className="h-screen flex bg-gray-50 pt-[60px]">
        <IconSidebar />
        
        <div className="flex-1 relative overflow-hidden">
          {/* 1. 背景となるメインコンテンツエリア */}
          <div className="absolute inset-0 p-6 overflow-auto">
            {children}
          </div>

          {/* 2. 前面に表示されるパネルエリア */}
          <div ref={scrollContainerRef} className="absolute inset-0 overflow-x-auto panel-viewport">
            <div className="h-full flex w-max">
              {/* --- 診療フローのパネル --- */}
              <AnimatePresence mode="wait">
                {showWorkflowStaffListPanel && <StaffListPanel key={`workflow-staff-list-${workflowStaffListRevision}`} context="workflow" />}
              </AnimatePresence>
              <AnimatePresence>
                {showDatePanel && <MonthCalendarPanel key={`date-panel-${datePanelRevision}`} context="workflow" />}
              </AnimatePresence>


              {/* --- 設定フローのパネル --- */}
              {/* PC では exit アニメなし → AnimatePresence を外して即座に DOM 削除。
                  enter アニメ (initial→animate) は AnimatePresence なしでも動く。
                  AnimatePresence があると exit=undefined でも 1-2 フレーム要素が残り、
                  rAF より後に除去されるため flex で押し出し（残像）が起きる。 */}
              {showSettingsPanel && <SettingsPanel key={`settings-panel-${settingsPanelRevision}`} />}
              {showSpeechDictionaryPanel && (
                <SpeechDictionaryPanel key={`speech-dict-${speechDictionaryRevision}`} />
              )}

              {/* 設定レーン: スタッフ一覧 → スタッフ編集 → 日付（設定） → 患者一覧（設定） */}
              {/* 残像防止: スタッフ一覧と患者一覧を同じ AnimatePresence(mode=wait) 配下にする */}
              <AnimatePresence mode="wait">
                {showStaffListPanel && <StaffListPanel key={`staff-list-${staffListRevision}`} context="settings" />}
                {showAllPatientListPanel && <PatientListPanel key={`all-patients-${patientListRevision}`} context="settings" />}
              </AnimatePresence>
              <AnimatePresence>
                {showStaffFormPanel && <StaffFormPanel key={`${editingStaff?.id || 'new'}-${staffFormRevision}`} />}
              </AnimatePresence>
              <AnimatePresence>
                {showSettingsDatePanel && <MonthCalendarPanel key={`settings-date-panel-${settingsDatePanelRevision}`} context="settings" />}
              </AnimatePresence>
              {/* 【重要】残像問題解決: AnimatePresenceを削除して残像を防止 */}
              {showSchedulePanel && <SchedulePanel key={`schedule-panel-${schedulePanelRevision}-${scheduleSelectedStaffId || 'no-staff'}`} />}
              <AnimatePresence mode="wait">
                {showSchedulePatientListPanel && (
                  <SchedulePatientListPanel key={`schedule-patients-${schedulePatientListRevision}`} />
                )}
              </AnimatePresence>

              {/* ▼▼▼ 【重要】患者登録パネル（設定/診療 共通）も wait で残像防止 ▼▼▼ */}
              <AnimatePresence mode="wait">
                {showPatientFormPanel && <PatientFormPanel key={`patient-form-${editingPatient?.id ?? 'new'}`} />}
              </AnimatePresence>
              {/* ▲▲▲ ここまで移動 ▲▲▲ */}

              

              {/* ▼▼▼ 【重要】申し送りパネルをここ（一番右）に移動 ▼▼▼ */}
              <AnimatePresence mode="wait">
                {showDetailPanel && <HandoverDetailPanel key={`detail-panel-${detailPanelRevision}`} />}
              </AnimatePresence>
              {/* ▲▲▲ ここまで移動 ▲▲▲ */}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};
